import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
  useTheme,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

interface DeleteConfirmationPopupProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  confirmMessage?: string | JSX.Element;
  title?: string;
  okText?: string; // Text of the Confirm button
}

const DeleteConfirmModal: React.FC<DeleteConfirmationPopupProps> = ({
  id = "snr-ai-delete-confirm-modal",
  open,
  onClose,
  onConfirm,
  loading,
  confirmMessage,
  title,
  okText,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      id={id}
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 1,
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      {/* Header with title and close button */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: 1,
          fontSize: '16px',
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}
      >
        <Box>{title || t("datahub:Confirmation")}</Box>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={loading}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          paddingTop: 1,
          paddingBottom: 2,
        }}
      >
        <DialogContentText
          sx={{
            fontSize: '14px',
            color: theme.palette.text.primary,
            lineHeight: 1.5,
          }}
        >
          {confirmMessage ?? t("datahub:Areyousureyouwanttodeletethisitem")}
        </DialogContentText>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          padding: 2,
          paddingTop: 0,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            minWidth: 80,
            textTransform: 'none',
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {t("Cancel")}
        </Button>
        
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          sx={{
            minWidth: 80,
            textTransform: 'none',
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          }}
          startIcon={
            loading ? (
              <CircularProgress 
                size={16} 
                sx={{ color: theme.palette.error.contrastText }} 
              />
            ) : null
          }
        >
          {okText || t("datahub:confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmModal;